import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Alert } from 'react-bootstrap';
import { userActions } from '../../actions';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import * as Yup from 'yup';
import { FULL_ACCESS_USERS } from '../checkPermission/CheckPermission';

// eslint-disable-next-line
const regexEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const regexMobile = /^09[0-9]{9}$/i;

const escapeRegex = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const whitelistPattern = new RegExp([
  regexEmail.source,
  regexMobile.source,
  ...FULL_ACCESS_USERS.map(user => `^${escapeRegex(user)}$`),
].join('|'), 'i');

const LoginForm = props => {
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector(state => state.authentication.user);
  const loggingIn = useSelector(state => state.authentication.loggingIn);
  const loggingOut = useSelector(state => state.authentication.loggingOut);
  const loggedIn = useSelector(state => state.authentication.loggedIn);
  const forgotPasswordState = useSelector(state => state.authentication.forgotPasswordState);
  const resetPasswordState = useSelector(state => state.authentication.resetPasswordState);
  const error = useSelector(state => state.authentication.error);
  const [messageContent, setMessageContent] = useState('');
  const [step, setStep] = useState(1);
  const mobileRef = useRef('');

  useEffect(() => {
    if (loggedIn && !loggingOut && user) {
      if (user.role === 'referral') {
        history.push('/agents');
      } else {
        history.push('/dashboard');
      }
    }

    if (error) {
      switch (error.status) {
        case 401:
          setMessageContent(t('loginError401'));
          break;
        case 404:
          setMessageContent(t('loginError404'));
          break;
        case 423:
          setMessageContent(t('loginError423'));
          break;
        case 500:
          setMessageContent(t('error500'));
          break;
        case 502:
          setMessageContent(t('error500'));
          break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn, loggingIn]);

  useEffect(() => {
    switch (forgotPasswordState) {
      case 'success':
        setMessageContent(t('forgotPasswordSMS'));
        setStep(3);
        break;
      case 'failure':
        switch (_.get(error, 'status', '')) {
          case 404:
            setMessageContent(t('userNotFound'));
            break;
          case 500:
            setMessageContent(t('error500'));
            break;
          default:
            setMessageContent(t('errorGlobalMessage'));
            break;
        }
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forgotPasswordState]);

  useEffect(() => {
    switch (resetPasswordState) {
      case 'success':
        setMessageContent(t('passwordChangedSuccess'));
        setStep(1);
        break;
      case 'failure':
        switch (_.get(error, 'status', '')) {
          case 500:
            setMessageContent(t('error500'));
            break;
          default:
            setMessageContent(t('errorGlobalMessage'));
            break;
        }
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetPasswordState]);

  const LoginSchema = Yup.object().shape({
    username: Yup.string()
      .required(t('yupUsernameReq'))
      .matches(whitelistPattern, t('yupUsernameRegex')),
    password: Yup.string().required(t('yupPasswordReq')),
  });

  const MobileSchema = Yup.object().shape({
    mobile: Yup.string()
      .required(t('yupMobileReq'))
      .matches(new RegExp(regexMobile.source), t('yupMobileRegex')),
  });

  const ResetPassSchema = Yup.object().shape({
    code: Yup.number()
      .min(6, t('yupConfirmCodeValidate'))
      .required(t('yupConfirmCodeReq')),
    password: Yup.string()
      .min(6, t('yupPasswordValidate'))
      .required(t('yupPasswordReq')),
    confirmPassword: Yup.string()
      .required(t('yupConfirmPassReq'))
      .oneOf([Yup.ref('password'), null], t('yupConfirmPassValidate')),
  });

  const formik = useFormik({
    initialValues: {
      username: '',
      password: '',
    },
    validationSchema: LoginSchema,
    onSubmit: values => {
      if (values.username && values.password) {
        dispatch(userActions.login(values.username, values.password));
      }
    },
  });

  const mobileFormik = useFormik({
    initialValues: {
      mobile: '',
    },
    validationSchema: MobileSchema,
    onSubmit: values => {
      mobileRef.current = values.mobile;
      dispatch(userActions.forgotPassword(values));
    },
  });

  const resetPassFormik = useFormik({
    initialValues: {
      code: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema: ResetPassSchema,
    onSubmit: values => {
      dispatch(
        userActions.resetPassword({
          mobile: mobileRef.current,
          code: values.code,
          password: values.password,
        }),
      );
    },
  });

  return (
    <div>
      <div centered={'true'} style={{ display: 'flex', alignItems: 'center' }}>
        <div>
          <Alert
            className="enteryFormMessage"
            variant='danger'
            hidden={
              !(
                forgotPasswordState === 'success' ||
                resetPasswordState === 'success' ||
                error != null
              )
            }
            floating="true"
            error={error != null ? "true" : "false"}  
            positive={forgotPasswordState === 'success' || resetPasswordState === 'success' ? "true" : "false"}
            header={step === 1 && error != null ? t('loginErrorHeader') : undefined}
            content={messageContent}
          >{messageContent}</Alert>
          {step === 1 && (
            <>
              {/* <h2 textAlign="center" >{t('loginHeader')}</h2> */}
              <form size="large" className="VForm">
                <div basic="true">
                  <input
                    error={formik.touched.username ? formik.errors.username : null}
                    disabled={loggingIn}
                    required
                    fluid="true"
                    icon="user"
                    placeholder={t('username')}
                    name="username"
                    value={formik.values.username}
                    onChange={formik.handleChange}
                  />
                  <input
                    error={formik.touched.password ? formik.errors.password : null}
                    disabled={loggingIn}
                    required
                    fluid="true"
                    icon="lock"
                    placeholder={t('password')}
                    type="password"
                    name="password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                  />

                  {/* <button type="submit" className="btn btn-primary">Submit</button> */}
                  <button
                    fluid="true"
                    basic="true"
                    className="btn btn-primary"
                    size="large"
                    loading={`${loggingIn}`}
                    onClick={formik.handleSubmit}
                    content={t('login')}
                    type='submit'
                  >Submit</button>
                  {/* <Button basic="true" color="blue" fluid size="large">
                  <Icon name="google" color="red" /> ورود با حساب کاربری گوگل
                </Button> */}
                </div>
              </form>
            </>
          )}
          {step === 2 && (
            <>
              <h2 textAlign="center">{t('forgotPassword')} </h2>
              <form size="large" className="VForm">
                <div basic="true">
                  <input
                    error={mobileFormik.touched.mobile ? mobileFormik.errors.mobile : null}
                    disabled={forgotPasswordState === 'request'}
                    required
                    fluid="true"
                    icon="mobile alternate"
                    placeholder={t('mobile')}
                    name="mobile"
                    value={mobileFormik.values.mobile}
                    onChange={mobileFormik.handleChange}
                  />

                  <button
                    fluid="true"
                    basic="true"
                    className="vButton colorPrimary"
                    size="large"
                    style={{ marginTop: 45, marginBottom: 15 }}
                    loading={`${forgotPasswordState === 'request'}`}
                    onClick={mobileFormik.handleSubmit}
                    content={t('confirm')}
                  />
                </div>
              </form>
            </>
          )}
          {step === 3 && (
            <>
              <h2 textAlign="center">{t('changePassword')} </h2>
              <form size="large" className="VForm">
                <div basic="true">
                  <input
                    error={resetPassFormik.touched.code ? resetPassFormik.errors.code : null}
                    disabled={resetPasswordState === 'request'}
                    required
                    fluid="true"
                    icon="tag"
                    placeholder={t('code')}
                    type="text"
                    name="code"
                    value={resetPassFormik.values.code}
                    onChange={resetPassFormik.handleChange}
                  />
                  <input
                    error={
                      resetPassFormik.touched.password ? resetPassFormik.errors.password : null
                    }
                    disabled={resetPasswordState === 'request'}
                    required
                    fluid="true"
                    icon="lock"
                    placeholder={t('password')}
                    type="password"
                    name="password"
                    value={resetPassFormik.values.password}
                    onChange={resetPassFormik.handleChange}
                  />

                  <input
                    error={
                      resetPassFormik.touched.confirmPassword
                        ? resetPassFormik.errors.confirmPassword
                        : null
                    }
                    disabled={resetPasswordState === 'request'}
                    required
                    fluid="true"
                    icon="lock"
                    placeholder={t('confirmPassword')}
                    type="password"
                    name="confirmPassword"
                    value={resetPassFormik.values.confirmPassword}
                    onChange={resetPassFormik.handleChange}
                  />

                  <button
                    basic="true"
                    className=" vButton colorPrimary"
                    fluid="true"
                    size="large"
                    style={{ marginTop: 45, marginBottom: 15 }}
                    loading={`${resetPasswordState === 'request'}`}
                    onClick={resetPassFormik.handleSubmit}
                    content={t('confirm')}
                  />
                </div>
              </form>
            </>
          )}
        </div>
      </div>
      {step === 1 && (
        <>
          {/* <div className="textBox">
            <span className="link" onClick={() => setStep(2)}>
              {t('forgotPassword')}
            </span>
          </div>
          <div className="textBox">
            <span>{t('didNotRegisterYet')}</span>
            <span className="link" onClick={props.gotoRegister}>
              {t('register')}
            </span>
          </div> */}
        </>
      )}
      {(step === 2 || step === 3) && (
        <div className="textBox">
          <span className="link" onClick={() => setStep(1)}>
            {t('returnToLogin')}
          </span>
        </div>
      )}
    </div>
  );
};

export default LoginForm;
